// The two charts under the board.
//
// FORM, decided before colour:
//
// 1. Installed vs not, by system. The job is magnitude compared across a
//    handful of named categories, with a part-to-whole inside each -> a
//    horizontal stacked bar per system. Horizontal because the category
//    labels are words ("Cable tray", "Bus duct") and words belong on a
//    horizontal axis, not rotated 45 degrees under a vertical one.
//
// 2. Install frequency over the project span, plus the S-curve. These are
//    the same measure (feet installed) at two aggregations, and their
//    scales differ by an order of magnitude - a day's work against the
//    job to date. That is the textbook case for a second y-axis, and a
//    second y-axis is the single worst thing you can do to a chart: the
//    crossing point where the two lines meet is an artefact of the two
//    scales, and readers take it for a fact. So they are TWO PANELS
//    sharing one x-axis instead, which is honest and just as compact.
//
// COLOUR: the four statuses are an ordinal ramp, one hue light->dark, in
// GREEN because installed means done - see trackingTheme.js for the ramp
// and its validation.
//
// The drawing carries a dash pattern as a second encoding; a stacked bar
// cannot (it is a fill, not a stroke), so here the separation rests on
// the ramp's own lightness steps, the legend, and the total labelled on
// each bar - all three present, none of them colour alone.
import React from "react";
import { Empty } from "antd";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ResponsiveContainer, AreaChart, Area, LabelList,
} from "recharts";
import {
  STATUS_ORDER, STATUS_COLOR, STATUS_LABEL, CHART_INK, systemLabel,
} from "./trackingTheme";

const ft = (n) => `${Math.round((Number(n) || 0) * 10) / 10}`;

// One tooltip style for both charts. Values are reachable in the item
// table too - a tooltip enhances, it never gates.
function ChartTip({ active, payload, label, unit = "ft" }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #E2E2E2", borderRadius: 6,
      boxShadow: "0 2px 10px rgba(35,52,62,.12)", padding: "8px 10px", fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, color: "#23343E", marginBottom: 4 }}>{label}</div>
      {payload.filter((p) => p.value).map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 8, lineHeight: 1.7 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: p.color || p.fill, flexShrink: 0 }} />
          <span style={{ color: "#3A4952", flex: 1 }}>{p.name}</span>
          <span style={{ color: "#23343E", fontVariantNumeric: "tabular-nums" }}>{ft(p.value)} {unit}</span>
        </div>
      ))}
    </div>
  );
}

const cardStyle = {
  background: "#fff", border: "1px solid #EFEFEF", borderRadius: 6, padding: "14px 16px",
};
const titleStyle = { fontSize: 12.5, fontWeight: 700, color: "#23343E", marginBottom: 2 };
const subStyle = { fontSize: 11, color: "#8D8D8D", marginBottom: 10 };

export default function TrackingCharts({ analytics, loading, narrowed = null }) {
  const bySystem = (analytics?.bySystem || []).filter((s) => s.total > 0);
  // The custom categories, as their own panel (owner, 2026-10-05: "now
  // show the custom categories on the construction tracking dashboard
  // too"). Never folded into the charts above: those count feet of board
  // item, these count PARTS inside model elements, and one bar holding
  // both would be two populations sharing an axis.
  const derived = (analytics?.derived || []).filter((d) => d && (d.n || d.tooManyParents));
  const follows = analytics?.derivedFollows || null;
  const timeline = analytics?.timeline || [];
  const projected = analytics?.projected || [];

  const systemRows = bySystem.map((s) => ({
    system: systemLabel(s.system),
    ...STATUS_ORDER.reduce((acc, st) => { acc[st] = (s.byStatus && s.byStatus[st]) || 0; return acc; }, {}),
    total: s.total,
  }));

  // Held at reduced opacity while refetching rather than replaced by a
  // skeleton: the filter row above changes these constantly, and a
  // skeleton flash on every change is a layout jump per keystroke.
  const holding = { opacity: loading ? 0.55 : 1, transition: "opacity .15s ease" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 12, ...holding }}>
      <div style={cardStyle}>
        <div style={titleStyle}>Installed and not, by system</div>
        <div style={subStyle}>Feet of run in each state. Full length of the bar is everything tracked for that system.</div>
        {!systemRows.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing with a length to measure yet" />
        ) : (
          // Height grows with the row count so the x-axis band is never
          // squeezed out of the container into a nested scrollbar.
          <ResponsiveContainer width="100%" height={Math.max(160, systemRows.length * 46 + 64)}>
            <BarChart data={systemRows} layout="vertical" margin={{ top: 4, right: 28, bottom: 4, left: 8 }} barCategoryGap="28%">
              <CartesianGrid horizontal={false} stroke={CHART_INK.grid} strokeWidth={1} />
              <XAxis type="number" tick={{ fontSize: 11, fill: CHART_INK.axis }} axisLine={{ stroke: CHART_INK.grid }} tickLine={false} unit=" ft" />
              <YAxis type="category" dataKey="system" width={96}
                     tick={{ fontSize: 11.5, fill: CHART_INK.text }} axisLine={false} tickLine={false} />
              <RTooltip content={<ChartTip />} cursor={{ fill: "rgba(35,52,62,.04)" }} />
              <Legend iconType="square" iconSize={9} wrapperStyle={{ fontSize: 11, color: CHART_INK.text, paddingTop: 6 }} />
              {STATUS_ORDER.map((st, i) => (
                <Bar
                  key={st} dataKey={st} stackId="s" name={STATUS_LABEL[st]}
                  fill={STATUS_COLOR[st]}
                  // A 2px surface gap between segments - a separator that
                  // is the background, not a border drawn around a mark.
                  stroke={CHART_INK.surface} strokeWidth={2}
                  radius={i === STATUS_ORDER.length - 1 ? [0, 4, 4, 0] : 0}
                >
                  {/* One label, on the end of the bar only: the total.
                      A number on every segment is unreadable, and the
                      per-status values are in the tooltip and the table. */}
                  {i === STATUS_ORDER.length - 1 && (
                    <LabelList dataKey="total" position="right"
                               formatter={(v) => `${ft(v)} ft`}
                               style={{ fontSize: 11, fill: CHART_INK.axis }} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {derived.length ? (
        <div style={cardStyle}>
          <div style={titleStyle}>Custom categories</div>
          <div style={subStyle}>
            Parts counted inside the elements above — beam clamps in the hangers, nuts in the
            trapezes. A part takes the status of the element it belongs to, so these move as that
            element does.{" "}
            <b>They are not included in the figures at the top of this page</b>, which count feet of
            tracked run: the parents are already counted there and counting their parts again would
            move the project percentage.
            {follows && narrowed && narrowed.length ? (
              <>
                {" "}This panel is the whole model and does not follow {narrowed.join(" or ")} —
                a part has no system of its own, and this is a reading of where things stand now.
              </>
            ) : null}
          </div>
          <div>
            {derived.map((d) => {
              // A ROW THAT COULD NOT BE RESOLVED SAYS SO, rather than
              // drawing a 0% bar that reads as "none of it is installed".
              if (d.tooManyParents) {
                return (
                  <div key={d.key} style={{ padding: "7px 0", borderTop: `1px solid ${CHART_INK.grid}` }}>
                    <div style={{ fontSize: 12, color: CHART_INK.text, fontWeight: 600 }}>{d.label}</div>
                    <div style={{ fontSize: 11, color: CHART_INK.axis }}>
                      {`${Number(d.tooManyParents).toLocaleString()} parent elements is more than this can measure at once`}
                    </div>
                  </div>
                );
              }
              const st = d.status || {};
              const at = (k) => (st[k] && st[k].n) || 0;
              const n = d.n || 0;
              const installed = at("installed");
              // THE DENOMINATOR IS SAID, every time. A bare percentage on a
              // dashboard is the number people quote in meetings.
              const pct = n ? Math.round((installed / n) * 1000) / 10 : 0;
              const len = (d.sums && d.sums.length) || 0;
              const lenInstalled = (st.installed && st.installed.length) || 0;
              const lenPct = len ? Math.round((lenInstalled / len) * 1000) / 10 : null;
              return (
                <div key={d.key} style={{ padding: "7px 0", borderTop: `1px solid ${CHART_INK.grid}` }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: CHART_INK.text, fontWeight: 600 }}>{d.label}</span>
                    {d.sourceCategory ? (
                      <span style={{ fontSize: 11, color: CHART_INK.axis }}>{`from ${d.sourceCategory}`}</span>
                    ) : null}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 12, color: CHART_INK.text, fontVariantNumeric: "tabular-nums" }}>
                      {`${pct}% of ${n.toLocaleString()} part${n === 1 ? "" : "s"}`}
                    </span>
                    {lenPct !== null ? (
                      <span style={{ fontSize: 11, color: CHART_INK.axis, fontVariantNumeric: "tabular-nums" }}>
                        {`· ${lenPct}% of ${ft(len)} ft`}
                      </span>
                    ) : null}
                  </div>
                  {/* The four statuses in one bar, same ordinal ramp as the
                      chart above, so a colour means the same thing in both. */}
                  <div style={{ display: "flex", height: 10, borderRadius: 3, overflow: "hidden", marginTop: 4, background: CHART_INK.grid }}>
                    {STATUS_ORDER.map((k) => {
                      const v = at(k);
                      if (!v) return null;
                      return (
                        <div
                          key={k}
                          title={`${STATUS_LABEL[k]}: ${v.toLocaleString()} part${v === 1 ? "" : "s"}`}
                          style={{ width: `${(v / (n || 1)) * 100}%`, background: STATUS_COLOR[k] }}
                        />
                      );
                    })}
                  </div>
                  {d.levels && d.levels.length ? (
                    <div style={{ fontSize: 10.5, color: CHART_INK.axis, marginTop: 3 }}>{d.levels.join(", ")}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div style={cardStyle}>
        <div style={titleStyle}>What is coming, and when</div>
        <div style={subStyle}>
          Segments still marked <b>projected</b>, on the date they are planned for. A forecast, not a
          record — kept on its own chart so a plan is never read as a fact.
        </div>
        {!projected.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing projected with a planned date in this window" />
        ) : (
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={projected} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid vertical={false} stroke={CHART_INK.grid} strokeWidth={1} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={{ stroke: CHART_INK.grid }} tickLine={false} minTickGap={24} />
              <YAxis tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={false} tickLine={false} width={44} unit=" ft" />
              <RTooltip content={<ChartTip />} cursor={{ fill: "rgba(35,52,62,.04)" }} />
              {/* The palest step of the same ramp: this is the "not started"
                  end of the scale, which is exactly what a plan is. */}
              <Bar dataKey="length" name="Projected to install" fill={STATUS_COLOR.projected} radius={[4, 4, 0, 0]} maxBarSize={22}>
                <LabelList dataKey="length" position="top" formatter={(v) => `${ft(v)}`}
                           style={{ fontSize: 10, fill: CHART_INK.axis }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={cardStyle}>
        <div style={titleStyle}>Install rate, and the job to date</div>
        <div style={subStyle}>
          Two panels on one time axis rather than two scales on one panel — a day's work and the
          running total are the same unit at very different magnitudes.
        </div>
        {!timeline.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No segments marked installed in this window" />
        ) : (
          <>
            <div style={{ fontSize: 11, color: CHART_INK.text, margin: "2px 0 2px" }}>Installed per day</div>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={timeline} margin={{ top: 4, right: 12, bottom: 4, left: 0 }} syncId="trackingTime">
                <CartesianGrid vertical={false} stroke={CHART_INK.grid} strokeWidth={1} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={{ stroke: CHART_INK.grid }} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={false} tickLine={false} width={44} unit=" ft" />
                <RTooltip content={<ChartTip />} cursor={{ fill: "rgba(35,52,62,.04)" }} />
                <Bar dataKey="length" name="Installed that day" fill={STATUS_COLOR.procured} radius={[4, 4, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: CHART_INK.text, margin: "10px 0 2px" }}>Cumulative installed</div>
            <ResponsiveContainer width="100%" height={170}>
              <AreaChart data={timeline} margin={{ top: 4, right: 12, bottom: 4, left: 0 }} syncId="trackingTime">
                <defs>
                  <linearGradient id="trkCum" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={STATUS_COLOR.installed} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={STATUS_COLOR.installed} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={CHART_INK.grid} strokeWidth={1} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={{ stroke: CHART_INK.grid }} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: CHART_INK.axis }} axisLine={false} tickLine={false} width={44} unit=" ft" />
                <RTooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="cumulative" name="Installed to date"
                      stroke={STATUS_COLOR.installed} strokeWidth={2} fill="url(#trkCum)"
                      dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }} />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}
